export interface User {
  id: string;
  email: string;
  name: string;
  role: "admin" | "manager" | "user";
  avatarUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Property {
  id: string;
  name: string;
  address: string;
  price: number;
  area: number;
  rooms: number;
  type: "apartment" | "house" | "land" | "office";
  status: "available" | "reserved" | "sold" | "rented";
  description?: string;
  images: string[];
  ownerId: string;
  createdAt: string;
  updatedAt: string;
}

export interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  type: "buyer" | "seller" | "tenant" | "landlord";
  status: "active" | "inactive" | "prospect";
  notes?: string;
  assignedToId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Transaction {
  id: string;
  propertyId: string;
  clientId: string;
  type: "sale" | "rent";
  amount: number;
  status: "pending" | "completed" | "cancelled";
  contractDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ─────────────────────────────────────────────────────────
// Unit 統合 (PropertySection / Equipment 廃止後の主体)
// BE shape と完全一致させる (interfaces/api/unitInterfaces.ts)
// ─────────────────────────────────────────────────────────

// area_type の単一ソース (BE: backend/src/constants/areaType.ts と一致)。
// 値・ラベル・options はすべてここから派生させる。
export const AREA_TYPES = [
  "private",
  "common",
  "facility",
  "land",
  "other",
] as const;
export type AreaType = (typeof AREA_TYPES)[number];
/**
 * 収益区分 (物件構成 Unit。FE 表示名「収益カテゴリ」)。
 * - stock_rent  : 貸室。月額契約による賃料収益。
 * - stock_other : 付帯。駐車場・看板・自販機等の付帯収益。配下に収益項目
 *                 (revenue_item_category_id) を 1 つ持つ。
 * - none        : 収益なし (共用部・通路等)。
 */
export type RevenueType = "none" | "stock_rent" | "stock_other";
export type OccupancyStatus = "occupied" | "vacant" | "out_of_service";

/** 顧客種別 (顧客/契約者マスタ) */
export type CustomerType = "individual" | "corporate";

/**
 * 顧客/契約者マスタ (Customers)。契約者を区画横断で束ねる正本 (client 配下・物件横断)。
 * Contract.customer_id から参照。現在値の正本＝最小フィールド (type別)。
 * 連絡先以外の事実 (住所/保証人等) は Contract スナップが正。
 */
export interface Customer {
  client_id?: string;
  customer_id: string;
  customer_type: CustomerType;
  // 個人
  last_name?: string;
  first_name?: string;
  // 法人
  company_name?: string;
  representative_last_name?: string;
  representative_first_name?: string;
  // 連絡先
  phone?: string;
  email?: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  version?: number;
}

/** 顧客の表示名: 個人=姓 名 / 法人=法人名 */
export const customerDisplayName = (c: {
  customer_type?: CustomerType;
  last_name?: string;
  first_name?: string;
  company_name?: string;
}): string =>
  c.customer_type === "corporate"
    ? c.company_name || ""
    : [c.last_name, c.first_name].filter(Boolean).join(" ");

export type CustomFieldType =
  | "text"
  | "number"
  | "date"
  | "select"
  | "checkbox"
  | "textarea"
  | "file";

/** 方角 (専有部・共用部の「向き」用) */
export type SpaceDirection =
  | "南"
  | "南東"
  | "東"
  | "北東"
  | "北"
  | "北西"
  | "西"
  | "南西";

/** 設置情報 1行 (項目名+台数)。専有部・共用部で共有 */
export interface EquipmentSpec {
  label: string;
  /** 入力フォーム上は文字列。永続化レイヤーで数値化する */
  count: string;
}

/**
 * 専有部の詳細情報。area_type=private の Unit のみが持つ。
 * 区画画像・関連資料 (区画画像のみ) も内包する。
 */
export interface PrivateDetail {
  images?: UnitImage[];
  floor?: string;
  area_sqm?: number;
  area_tsubo?: number;
  ceiling_height_m?: number;
  direction?: SpaceDirection;
  equipment_specs?: EquipmentSpec[];
  utility_collection?: {
    electricity?: boolean;
    water?: boolean;
    gas?: boolean;
  };
}

/**
 * 共用部の詳細情報。area_type=common の Unit のみが持つ。
 * 関連資料 (documents) も内包する。
 */
export interface CommonDetail {
  usage_description?: string;
  floor?: string;
  area_sqm?: number;
  area_tsubo?: number;
  ceiling_height_m?: number;
  direction?: SpaceDirection;
  equipment_specs?: EquipmentSpec[];
  documents?: UnitDocument[];
}

/**
 * 建物設備の詳細情報。area_type=facility の Unit のみが持つ。
 * 関連資料 (documents) も内包する。
 */
export interface FacilityDetail {
  manufacturer?: string;
  model_number?: string;
  manufactured_at?: string;
  installed_at?: string;
  capacity?: string;
  useful_life_years?: number;
  next_replacement_at?: string;
  documents?: UnitDocument[];
}

/**
 * 敷地の詳細情報。area_type=land の Unit のみが持つ。
 * land_type で出し分け:
 * - parking: 車室番号 / 駐車場種別 / 車高制限 / 車幅制限
 * - exterior / greenery: 舗装種別 / 最終補修年月
 * - other: 詳細フィールドなし
 */
export interface LandDetail {
  land_type?: LandType;
  // 駐車場
  lot_number?: string;
  parking_subtype?: ParkingSubtype;
  height_limit_m?: number;
  width_limit_m?: number;
  // 外構 / 緑地
  paving_type?: PavingType;
  /** 最終補修年月 (YYYY-MM) */
  last_repair_at?: string;
}

// ─────────────────────────────────────────────────────────
// LandDetail で使う種別 / ラベル / options
// ─────────────────────────────────────────────────────────

export type LandType = "parking" | "exterior" | "greenery" | "other";
export type PavingType = "asphalt" | "concrete" | "gravel" | "other";
export type ParkingSubtype = "flat" | "multi" | "mechanical" | "other";

export const LAND_TYPE_LABELS: Record<LandType, string> = {
  parking: "駐車場",
  exterior: "外構",
  greenery: "緑地",
  other: "その他",
};

export const PARKING_SUBTYPE_LABELS: Record<ParkingSubtype, string> = {
  flat: "平面",
  multi: "立体",
  mechanical: "機械式",
  other: "その他",
};

export const PARKING_SUBTYPE_OPTIONS: ReadonlyArray<{
  label: string;
  value: ParkingSubtype;
}> = (Object.keys(PARKING_SUBTYPE_LABELS) as ParkingSubtype[]).map((k) => ({
  label: PARKING_SUBTYPE_LABELS[k],
  value: k,
}));

export const LAND_TYPE_OPTIONS: ReadonlyArray<{
  label: string;
  value: LandType;
}> = (Object.keys(LAND_TYPE_LABELS) as LandType[]).map((k) => ({
  label: LAND_TYPE_LABELS[k],
  value: k,
}));

export const PAVING_TYPE_LABELS: Record<PavingType, string> = {
  asphalt: "アスファルト",
  concrete: "コンクリート",
  gravel: "砂利",
  other: "その他",
};

export const PAVING_TYPE_OPTIONS: ReadonlyArray<{
  label: string;
  value: PavingType;
}> = (Object.keys(PAVING_TYPE_LABELS) as PavingType[]).map((k) => ({
  label: PAVING_TYPE_LABELS[k],
  value: k,
}));

/** 予算 (revenue_type が stock 系: stock_rent / stock_other のみ) */
export interface BudgetInfo {
  // ── 契約条件 (テナント管理 / ContractSection で扱う既存フィールド) ──
  start_date?: string;
  end_date?: string;
  expected_rent?: number;
  rent_unit_price?: number;
  common_fee?: number;
  maintenance_fee_unit_price?: number;
  pricing_type?: "fixed" | "unit_quantity";
  unit_price?: number;
  quantity?: number;
  quantity_unit?: string;
  deposit_months?: number;
  key_money_months?: number;
  contract_form?: string;
  rent_start_date?: string;
  /** 敷金 (円) */
  deposit_amount?: number;
  /** 償却後敷金 (円) */
  deposit_after_amount?: number;
  /** 礼金 (円) */
  key_money_amount?: number;

  // ── 区画詳細の「予算」セクション (予実反映用) ──
  /** 月額賃料予算 (BudgetCategory「賃料収入」に集計) */
  monthly_rent?: number;
  /** 月額共益費予算 (BudgetCategory「共益費収入」に集計) */
  monthly_common_fee?: number;
  /** 区画ごとの追加予算行 (任意の BudgetCategory に月次/単発で計上) */
  extra_items?: Array<{
    item_id: string;
    /** BudgetCategory.category_id (必須) */
    category_id: string;
    frequency: "monthly" | "one_time";
    amount: number;
    /** one_time のみ "YYYY-MM" */
    target_month?: string;
    memo?: string;
  }>;
}

export interface StockRevenueInfo {
  rent_amount?: number;
  management_fee?: number;
  recruitment_status?: "active" | "suspended";
  rent_unit_price?: number;
  mgmt_unit_price?: number;
  current_contract_id?: string;
}

// ─────────────────────────────────────────────────────────
// Contracts テーブル (Unit から契約者情報を分離)
// ─────────────────────────────────────────────────────────

export type ContractStatus = "active" | "ended" | "upcoming";

export interface Contract {
  client_id: string;
  contract_id: string;
  unit_id: string;
  property_id: string;

  contractor_type: "individual" | "corporate";
  contractor_name: string;
  company_name?: string;
  contact_person?: string;
  phone?: string;
  email?: string;
  address?: string;

  emergency_contact?: { name: string; phone: string };
  guarantor?: { name: string; phone: string };
  resident_info?: string;
  vehicle?: {
    manufacturer?: string;
    model?: string;
    plate?: string;
    color?: string;
  };

  start_date: string;
  end_date?: string;
  rent_start_date?: string;
  cancellation_date?: string;

  monthly_rent?: number;
  rent_unit_price?: number;
  maintenance_fee?: number;
  maintenance_fee_unit_price?: number;
  pricing_type?: "fixed" | "unit_quantity";
  unit_price?: number;
  quantity?: number;
  quantity_unit?: string;
  security_deposit?: number;
  security_deposit_after?: number;
  key_money?: number;
  other_fees?: Array<{ label: string; amount: number }>;

  contract_form?: string;
  contract_remarks?: string;
  status: ContractStatus;

  /** 契約書類 (賃貸借契約書 / 身分証明書 等。Unit.documents とは独立) */
  documents?: ContractDocument[];

  departure_date?: string;
  departure_reason?: string;

  created_at: string;
  updated_at?: string;
  created_by: string;
  created_by_name?: string;
  updated_by?: string;
  updated_by_name?: string;
}

export interface ContractDocument {
  document_id: string;
  category: string;
  label?: string;
  file_name: string;
  s3_key: string;
  uploaded_at: string;
}

export interface ContractInfo {
  contractor_type: "individual" | "corporate";
  contractor_name: string;
  /** 個人 姓 */
  last_name?: string;
  /** 個人 名 */
  first_name?: string;
  company_name?: string;
  contact_person?: string;
  /** 法人代表者 姓 */
  representative_last_name?: string;
  /** 法人代表者 名 */
  representative_first_name?: string;
  address?: string;
  phone?: string;
  email?: string;
  deposit_months?: number;
  key_money_months?: number;
  /** 備考 (新規契約モーダルと揃え) */
  note?: string;
  emergency_contact?: { name: string; phone: string };
  guarantor?: { name: string; phone: string };
  resident_info?: string;
  vehicle?: {
    manufacturer?: string;
    model?: string;
    plate?: string;
    color?: string;
  };
}

export interface CustomFieldValue {
  field_id: string;
  value: string | number | boolean | string[] | null;
}

export interface UnitDocument {
  document_id: string;
  category: "spec" | "manual" | "contract" | "other";
  /** カスタムラベル (区画詳細「添付書類」セクションで自由入力) */
  label?: string;
  file_name: string;
  s3_key: string;
  uploaded_at: string;
}

/** 区画画像 (添付書類とは別管理) */
export interface UnitImage {
  image_id: string;
  file_name: string;
  s3_key: string;
  uploaded_at: string;
}

export interface Unit {
  client_id: string;
  unit_id: string;
  property_id: string;
  area_sort: string;
  revenue_sort: string;
  area_type: AreaType;
  /** 名称 (例: 101号室、EV 1号機、P-3) */
  landmark_name: string;
  occupancy_status: OccupancyStatus;
  display_order?: number;
  /** 駐車場として扱うか (場所区分問わず) */
  is_parking?: boolean;
  revenue_type: RevenueType;
  /**
   * 収益項目 (付帯=stock_other のときのみ)。BudgetCategory.category_id への参照
   * (category_type=income, revenue_group=stock_other: 駐車場/看板/自販機/アンテナ/水光熱費 等)。
   */
  revenue_item_category_id?: string;
  /** 紐づく顧客 (Customer.customer_id)。既存顧客から登録時に紐づく */
  customer_id?: string;

  // 区画情報セクション直下フィールド (参照画面準拠)
  floor?: string;
  floor_plan?: string;
  notes?: string;
  /** 管理責任者 (Employee.employee_id) */
  manager_employee_id?: string;

  // ── 場所区分ごとの詳細情報。area_type に応じてどれか1つだけが有効 ──
  private_detail?: PrivateDetail;
  common_detail?: CommonDetail;
  facility_detail?: FacilityDetail;
  land_detail?: LandDetail;

  budget?: BudgetInfo;
  revenue?: StockRevenueInfo;
  contract?: ContractInfo;
  custom_fields?: CustomFieldValue[];
  documents?: UnitDocument[];
  images?: UnitImage[];
  /** 現在有効な契約 ID (Contracts テーブル参照ポインタ) */
  current_contract_id?: string;
  created_at: string;
  updated_at?: string;
  deleted_at?: string;
}

export interface UnitSubtypeMaster {
  client_id: string;
  sort_key: string;
  subtype_id: string;
  property_id: string;
  area_type: AreaType;
  code: string;
  label: string;
  is_system: boolean;
  display_order?: number;
  created_at: string;
  updated_at?: string;
  deleted_at?: string;
}

/**
 * カスタムフォーム設定で扱うセクション key (Unit フォームのセクション)。
 * 「区画情報 (basic)」と「詳細情報 (detail)」の2セクションに集約する。
 */
export type UnitCustomFieldSection = "basic" | "detail";

export interface UnitCustomField {
  client_id: string;
  field_id: string;
  label: string;
  field_type: CustomFieldType;
  options?: string[];
  display_order?: number;
  /** どのセクションに配置するか (未設定は basic 扱い) */
  section_key?: UnitCustomFieldSection;
  /** 必須項目フラグ */
  required?: boolean;
  created_at: string;
  updated_at?: string;
}

/**
 * Unit subtype の system 定数。BE の constants/unitSubtypes.ts と一致。
 * code は **不変** (rename はしない)。新たな subtype は UnitSubtypeMaster に custom 登録。
 */
export const SYSTEM_SUBTYPES: Record<
  AreaType,
  Array<{ code: string; label: string }>
> = {
  private: [
    { code: "residential", label: "居室" },
    { code: "shop", label: "店舗" },
  ],
  common: [
    { code: "entrance", label: "エントランス" },
    { code: "hallway", label: "廊下" },
    { code: "stairs", label: "階段" },
    { code: "ev_hall", label: "EVホール" },
    { code: "common_toilet", label: "共用トイレ" },
    { code: "trash_yard", label: "ゴミ置場" },
    { code: "machine_room", label: "機械室" },
    { code: "other_common", label: "その他共用" },
  ],
  facility: [
    { code: "elevator", label: "EV" },
    { code: "transformer", label: "受変電" },
    { code: "water_tank", label: "貯水槽" },
    { code: "solar", label: "太陽光発電" },
    { code: "other_facility", label: "その他建物設備" },
  ],
  land: [
    { code: "parking_flat", label: "駐車場(平面)" },
    { code: "parking_multi", label: "駐車場(立体)" },
    { code: "parking_mech", label: "駐車場(機械式)" },
    { code: "bicycle", label: "駐輪場" },
    { code: "motorbike", label: "バイク置き場" },
    { code: "trunk_room", label: "トランクルーム" },
    { code: "exterior", label: "外構" },
    { code: "planting", label: "植栽" },
    { code: "signboard", label: "屋外看板" },
    { code: "container", label: "コンテナ" },
  ],
  other: [
    { code: "rooftop", label: "屋上" },
    { code: "outer_wall", label: "外壁" },
    { code: "foundation", label: "基礎" },
    { code: "balcony", label: "バルコニー" },
  ],
};

export const AREA_TYPE_LABELS: Record<AreaType, string> = {
  private: "専有部",
  common: "共用部",
  facility: "建物設備",
  land: "敷地",
  other: "その他",
};

/** 場所区分セレクトの options (label: 日本語表示, value: AreaType code)。
 *  Object.keys ではなく AREA_TYPES から派生させて宣言順を保つ。 */
export const AREA_TYPE_OPTIONS: ReadonlyArray<{
  label: string;
  value: AreaType;
}> = AREA_TYPES.map((k) => ({
  label: AREA_TYPE_LABELS[k],
  value: k,
}));
